import { POKE_DATA } from "game/data/pokemon/data";
import { calculatePokemonPower } from "game/data/pokemon/power-calculator";
import { PokeData } from "types/pokemon/poke-data";
import { PokeNames } from "types/pokemon/poke-names";

/**
 * core-packs (C2) pack tier. A base-stage species' OWN power can't tell junk from
 * premium apart (e.g. Rattata/Beldum/Dratini score almost identically) -- see
 * `classifyPackTier` for how catch_rate and family peak power resolve that.
 */
export enum PackTier {
  JUNK = "JUNK",
  STANDARD = "STANDARD",
  STRONG = "STRONG",
  PREMIUM = "PREMIUM",
}

const JUNK_CATCH_RATE_THRESHOLD = 200;
const PREMIUM_CATCH_RATE_THRESHOLD = 10;
const PREMIUM_FAMILY_PEAK_POWER_THRESHOLD = 380;
const STRONG_OWN_POWER_THRESHOLD = 190;

/**
 * Highest `calculatePokemonPower` across every stage of a species' evolution family.
 * Only used to catch "weak baby, strong adult" lines (e.g. Dratini -> Dragonite) for
 * the PREMIUM check below -- never used to name, flavor, or grant anything beyond the
 * base stage itself (packs always grant the base stage per C2's locked decision).
 */
export const getFamilyPeakPower = (pokeData: PokeData): number => {
  return pokeData.evolution_chain.species.reduce((peak, stage) => {
    const stageData = POKE_DATA[stage.name as PokeNames] as PokeData | undefined;
    if (!stageData) return peak;
    return Math.max(peak, calculatePokemonPower(stageData));
  }, 0);
};

/**
 * Classifies a base-stage species into a C2 pack tier.
 * Priority order: JUNK (commonness) -> PREMIUM (rarity or evolved ceiling) -> STRONG
 * (already decent alone) -> STANDARD (everything else).
 */
export const classifyPackTier = (pokeData: PokeData): PackTier => {
  if (pokeData.catch_rate >= JUNK_CATCH_RATE_THRESHOLD) {
    return PackTier.JUNK;
  }

  if (
    pokeData.catch_rate <= PREMIUM_CATCH_RATE_THRESHOLD ||
    getFamilyPeakPower(pokeData) >= PREMIUM_FAMILY_PEAK_POWER_THRESHOLD
  ) {
    return PackTier.PREMIUM;
  }

  if (calculatePokemonPower(pokeData) >= STRONG_OWN_POWER_THRESHOLD) {
    return PackTier.STRONG;
  }

  return PackTier.STANDARD;
};
